import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { STORAGE_KEYS, MAX_SAMPLE_ITEMS } from '../utils/constants';

export { MAX_SAMPLE_ITEMS };

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.CART);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Persist to localStorage on every change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.CART, JSON.stringify(items));
    } catch {
      // ignore
    }
  }, [items]);

  const addItem = useCallback(({ productVariantId, quantity, product, variant, is_sample = false }) => {
    setItems(prev => {
      // For sample items, match on both variantId and is_sample flag
      // so a variant can appear as both a purchase and a sample
      const existingIndex = prev.findIndex(
        item => item.productVariantId === productVariantId && item.is_sample === is_sample
      );

      if (is_sample) {
        // Already have this variant as a sample — don't add duplicates
        if (existingIndex !== -1) return prev;
        // Enforce max distinct sample line items
        const currentSampleCount = prev.filter(item => item.is_sample).length;
        if (currentSampleCount >= MAX_SAMPLE_ITEMS) return prev;
      } else if (existingIndex !== -1) {
        const existing = prev[existingIndex];
        const newQty = existing.quantity + quantity;
        return prev.map((item, i) =>
          i === existingIndex
            ? { ...item, quantity: newQty, subtotal: (parseFloat(item.unit_price) || 0) * newQty }
            : item
        );
      }

      const unitPrice = is_sample ? 0 : (parseFloat(variant.price_dollars ?? variant.price) || 0);
      return [
        ...prev,
        {
          id: `temp-${Date.now()}`,
          productVariantId,
          quantity: is_sample ? 1 : quantity,
          is_sample,
          product: { id: product.id, name: product.name, image_url: product.image_url },
          variant: {
            id: variant.id,
            variant_name: variant.variant_name,
            sku: variant.sku,
            price: variant.price_dollars ?? variant.price,
            unit: variant.unit,
          },
          unit_price: unitPrice,
          subtotal: unitPrice * (is_sample ? 1 : quantity),
        },
      ];
    });
  }, []);

  const updateItem = useCallback((itemId, newQuantity) => {
    if (newQuantity < 1) {
      setItems(prev => prev.filter(item => item.id !== itemId));
      return;
    }
    setItems(prev =>
      prev.map(item =>
        item.id === itemId
          ? { ...item, quantity: newQuantity, subtotal: (parseFloat(item.unit_price) || 0) * newQuantity }
          : item
      )
    );
  }, []);

  const removeItem = useCallback((itemId) => {
    setItems(prev => prev.filter(item => item.id !== itemId));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    try {
      localStorage.removeItem(STORAGE_KEYS.CART);
    } catch {
      // ignore
    }
  }, []);

  const total = items.reduce((sum, item) => {
    if (item.is_sample) return sum;
    const subtotal = parseFloat(item.subtotal);
    return sum + (Number.isFinite(subtotal) ? subtotal : 0);
  }, 0);
  const sampleItemCount = items.filter(item => item.is_sample).length;

  const value = useMemo(
    () => ({ items, addItem, updateItem, removeItem, clearCart, total, sampleItemCount }),
    [items, addItem, updateItem, removeItem, clearCart, total, sampleItemCount]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
};
